import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useState, useMemo, useCallback } from 'react';
import {
  ChevronRight,
  RefreshCw,
  Calendar,
  ArrowRightLeft,
  X as XIcon,
  Minus,
  Plus,
  HelpCircle,
  MessageCircle,
} from 'lucide-react';
import type { CustomerSubscription } from '@/domain/entities/subscription.entity';
import { useProductImageUrl, useVariantAttributes } from '@/lib/hooks/use-product';

// -- Helpers --

function getDaysUntilNextDelivery(nextChargeDate: string | null): string {
  if (!nextChargeDate) return 'N/A';
  const now = new Date();
  const next = new Date(nextChargeDate);
  const diffMs = next.getTime() - now.getTime();
  if (diffMs <= 0) return 'Delivery today';
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return `${days} days until next delivery`;
}

function formatDeliveryDate(nextChargeDate: string | null): string {
  if (!nextChargeDate) return '';
  const date = new Date(nextChargeDate);
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const yy = String(date.getUTCFullYear()).slice(-2);
  return `(${mm}/${dd}/${yy})`;
}

function formatFrequencyLabel(frequencyDays: number): string {
  if (frequencyDays <= 31) return 'Deliver every month';
  if (frequencyDays <= 62) return 'Deliver every 2 months';
  if (frequencyDays <= 93) return 'Deliver every 3 months';
  if (frequencyDays <= 124) return 'Deliver every 4 months';
  if (frequencyDays <= 186) return 'Deliver every 6 months';
  return 'Deliver every year';
}

// -- FAQ data from Figma --

const AUTOSHIP_FAQS = [
  {
    question: 'What is Autoship?',
    answer:
      "Autoship is a fantastic subscription service from Shapermint where you can save 60% on select products and have them conveniently delivered to your home every 3 or 6 months. It's a great way to subscribe and save, ensuring you always have your favorite items on hand at the lowest price available in the store.",
  },
  {
    question: 'How does an Autoship order work?',
    answer:
      "An Autoship order is all about convenience and savings. Once you opt-in, you'll save 60% on your selected product, and we'll automatically schedule it to be sent to you every 3 or 6 months based on your preference. Your credit card will be billed accordingly each cycle, and the product will be shipped directly to your home.",
  },
  {
    question:
      "I like the product in my Autoship order but it's the wrong size, can I exchange it?",
    answer:
      'Absolutely! If the size isn\'t quite right, you can easily update it for your next Autoship order. Just reach out to our customer care team via chat or email, and they\'ll help you adjust your size preferences to make sure your next delivery is perfect. You can even change the colors! If you already know the size you need, simply click "Swap Product" to update it.',
  },
];

// -- Styled Components --

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(41, 41, 41, 0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;

  @media (max-width: 767px) {
    align-items: flex-end;
  }
`;

const ModalPanel = styled.div`
  width: 100%;
  max-width: 860px;
  background: #fff;
  border-radius: 16px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 40px;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${scaleIn} 0.3s ease-out;

  @media (max-width: 767px) {
    max-width: 100%;
    border-radius: 16px 16px 0 0;
    padding: 24px 16px;
    max-height: 95vh;
    animation: ${slideUp} 0.3s ease-out;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #292929;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  &:hover {
    opacity: 0.7;
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ProductImageBanner = styled.div`
  width: 100%;
  height: 200px;
  background: #f0f0f0;
  border-radius: 8px 8px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #767676;
  font-size: 14px;
  overflow: hidden;
`;

const ProductImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const SavingsBanner = styled.div`
  background: #2f806a;
  padding: 12px 16px;
  text-align: center;
`;

const SavingsText = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
  color: #fff;
`;

const DetailsCard = styled.div`
  background: #f7f7f7;
  border-radius: 0 0 8px 8px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProductTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #292929;
  margin: 0;
`;

const DeliveryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const DeliveryText = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: #292929;
`;

const DeliveryChevron = styled.span`
  display: inline-flex;
  align-items: center;
  color: #292929;
  flex-shrink: 0;
`;

const FrequencyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #292929;
`;

const FrequencyIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: #292929;
`;

const FrequencyText = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.33;
  color: #292929;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.57;
  color: #292929;
`;

const DetailValueGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: #292929;
`;

const ChangeLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.57;
  color: #292929;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const ActionListSection = styled.div`
  background: #fff;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
`;

const ActionItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: none;
  border: none;
  border-bottom: 1px solid #e5e5e5;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
`;

const ActionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #292929;
  flex-shrink: 0;
`;

const ActionContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ActionLabel = styled.span`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.56;
  color: #292929;
`;

const ActionChevron = styled.span`
  display: flex;
  align-items: center;
  color: #292929;
  flex-shrink: 0;
`;

const SectionCard = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e5e5e5;
`;

const SectionTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.56;
  color: #292929;
  margin: 0;
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const AccordionTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #fafafa;
  }
`;

const AccordionQuestion = styled.span`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #292929;
  flex: 1;
`;

const AccordionContent = styled.div`
  padding: 0 16px 16px;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: #292929;
`;

const CareItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: none;
  border: none;
  border-bottom: 1px solid #e5e5e5;
  cursor: pointer;
  text-align: left;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
`;

const CareIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #292929;
  flex-shrink: 0;
`;

const CareContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CareLabel = styled.span`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.56;
  color: #292929;
`;

const Spacer = styled.div<{ size?: number }>`
  height: ${({ size = 24 }) => size}px;
`;

const SkippedTag = styled.span`
  display: inline-block;
  background: #ffe1b8;
  color: #292929;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 8px 2px;
  border-radius: 2px;
  line-height: 1;
`;

// -- Component --

interface ManageSubscriptionPageProps {
  readonly subscription: CustomerSubscription;
  readonly onCancel: (subscription: CustomerSubscription) => void;
  readonly onSkip?: (subscription: CustomerSubscription) => void;
  readonly onReschedule?: (subscription: CustomerSubscription) => void;
  readonly onSwapProduct?: (subscription: CustomerSubscription) => void;
  readonly onChangeVariant?: (subscription: CustomerSubscription) => void;
  readonly onClose: () => void;
}

export function ManageSubscriptionPage({
  subscription,
  onCancel,
  onSkip,
  onReschedule,
  onSwapProduct,
  onChangeVariant,
  onClose,
}: ManageSubscriptionPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const firstItem = subscription.items?.[0];
  const productName = firstItem?.name ?? subscription.plan.name;
  const imageUrl = useProductImageUrl(firstItem?.product_id, firstItem?.image_url);

  const matchedFrequency = subscription.plan.frequencies?.find(
    (f) => f.id === subscription.plan_frequency_id,
  );
  const frequencyDays = matchedFrequency?.frequency_days ?? 30;

  const priceDisplay = useMemo(() => {
    if (!matchedFrequency) return 'N/A';
    return `$${(matchedFrequency.price_display / 100).toFixed(2)}`;
  }, [matchedFrequency]);

  const deliveryCountdown = getDaysUntilNextDelivery(subscription.next_charge_date);
  const deliveryDate = formatDeliveryDate(subscription.next_charge_date);

  const { size: variantSize, color: variantColor } = useVariantAttributes(
    firstItem?.product_id,
    firstItem?.variation_id,
  );
  const size = variantSize ?? 'N/A';
  const color = variantColor ?? 'N/A';

  const isSkippable = subscription.plan.is_skippable;
  const isAutoship = subscription.plan.type === 'autoship';
  // Variant changes hit updateSubscriptionItems, which the backend rejects
  // with ErrAutoshipOnly for membership plans.
  const canChangeVariant = isAutoship && onChangeVariant !== undefined;

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return (
    <Backdrop onClick={handleBackdropClick}>
      <ModalPanel>
        <CloseButton onClick={onClose} aria-label="Close">
          <XIcon size={24} />
        </CloseButton>

        <ModalBody>
      {/* Product card with image + savings + details */}
      <ProductImageBanner>
        {imageUrl ? (
          <ProductImg src={imageUrl} alt={productName} />
        ) : (
          'Product Image'
        )}
      </ProductImageBanner>

      <SavingsBanner>
        <SavingsText>You have saved $60 in the last 3 months</SavingsText>
      </SavingsBanner>

      <DetailsCard>
        <ProductTitle>{productName}</ProductTitle>

        {subscription.skipped_charge_date && (
          <SkippedTag data-testid="skipped-tag">Skipped</SkippedTag>
        )}

        <DeliveryRow>
          <DeliveryText>
            {deliveryCountdown} {deliveryDate}
          </DeliveryText>
          <DeliveryChevron>
            <ChevronRight size={16} />
          </DeliveryChevron>
        </DeliveryRow>

        <FrequencyRow>
          <FrequencyIcon>
            <RefreshCw size={14} />
          </FrequencyIcon>
          <FrequencyText>{formatFrequencyLabel(frequencyDays)}</FrequencyText>
        </FrequencyRow>

        <DetailRow>
          <DetailLabel>Size</DetailLabel>
          <DetailValueGroup>
            <DetailValue>{size}</DetailValue>
            {canChangeVariant && (
              <ChangeLink
                type="button"
                aria-label="Change size"
                onClick={() => onChangeVariant?.(subscription)}
              >
                Change
              </ChangeLink>
            )}
          </DetailValueGroup>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Color</DetailLabel>
          <DetailValueGroup>
            <DetailValue>{color}</DetailValue>
            {canChangeVariant && (
              <ChangeLink
                type="button"
                aria-label="Change color"
                onClick={() => onChangeVariant?.(subscription)}
              >
                Change
              </ChangeLink>
            )}
          </DetailValueGroup>
        </DetailRow>

        <DetailRow>
          <DetailLabel>Total</DetailLabel>
          <DetailValue>{priceDisplay}</DetailValue>
        </DetailRow>
      </DetailsCard>

      <Spacer size={8} />

      {/* Action menu list */}
      <ActionListSection>
        {isAutoship && onSwapProduct && (
          <ActionItem
            type="button"
            aria-label="Swap product"
            onClick={() => onSwapProduct(subscription)}
          >
            <ActionIcon>
              <ArrowRightLeft size={20} />
            </ActionIcon>
            <ActionContent>
              <ActionLabel>Swap product</ActionLabel>
              <ActionChevron>
                <ChevronRight size={18} />
              </ActionChevron>
            </ActionContent>
          </ActionItem>
        )}

        <ActionItem
          type="button"
          aria-label="Reschedule"
          onClick={() => onReschedule?.(subscription)}
          data-testid="reschedule-action"
        >
          <ActionIcon>
            <Calendar size={20} />
          </ActionIcon>
          <ActionContent>
            <ActionLabel>Reschedule</ActionLabel>
            <ActionChevron>
              <ChevronRight size={18} />
            </ActionChevron>
          </ActionContent>
        </ActionItem>

        {isSkippable && (
          <ActionItem
            type="button"
            aria-label="Skip"
            onClick={() => onSkip?.(subscription)}
          >
            <ActionIcon>
              <RefreshCw size={20} />
            </ActionIcon>
            <ActionContent>
              <ActionLabel>Skip</ActionLabel>
              <ActionChevron>
                <ChevronRight size={18} />
              </ActionChevron>
            </ActionContent>
          </ActionItem>
        )}

        <ActionItem
          type="button"
          aria-label="Cancel Subscription"
          onClick={() => onCancel(subscription)}
        >
          <ActionIcon>
            <XIcon size={20} />
          </ActionIcon>
          <ActionContent>
            <ActionLabel>Cancel Subscription</ActionLabel>
            <ActionChevron>
              <ChevronRight size={18} />
            </ActionChevron>
          </ActionContent>
        </ActionItem>
      </ActionListSection>

      <Spacer size={24} />

      {/* FAQs accordion */}
      <SectionCard>
        <SectionHeader>
          <SectionTitle>FAQs</SectionTitle>
        </SectionHeader>
        {AUTOSHIP_FAQS.map((faq, index) => (
          <AccordionItem key={faq.question}>
            <AccordionTrigger
              type="button"
              aria-expanded={openFaq === index}
              onClick={() => setOpenFaq(openFaq === index ? null : index)}
            >
              <AccordionQuestion>{faq.question}</AccordionQuestion>
              {openFaq === index ? <Minus size={16} /> : <Plus size={16} />}
            </AccordionTrigger>
            {openFaq === index && (
              <AccordionContent>{faq.answer}</AccordionContent>
            )}
          </AccordionItem>
        ))}
      </SectionCard>

      <Spacer size={24} />

      {/* Customer Care section */}
      <SectionCard>
        <SectionHeader>
          <SectionTitle>Customer Care</SectionTitle>
        </SectionHeader>
        <CareItem type="button" aria-label="Help Center">
          <CareIcon>
            <HelpCircle size={20} />
          </CareIcon>
          <CareContent>
            <CareLabel>Help Center</CareLabel>
            <ActionChevron>
              <ChevronRight size={18} />
            </ActionChevron>
          </CareContent>
        </CareItem>
        <CareItem type="button" aria-label="Support Chat">
          <CareIcon>
            <MessageCircle size={20} />
          </CareIcon>
          <CareContent>
            <CareLabel>Support Chat</CareLabel>
            <ActionChevron>
              <ChevronRight size={18} />
            </ActionChevron>
          </CareContent>
        </CareItem>
      </SectionCard>

      <Spacer size={32} />
        </ModalBody>
      </ModalPanel>
    </Backdrop>
  );
}
